"use client";

import { useLocale } from "@calcom/lib/hooks/useLocale";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { ReactElement } from "react";
import { useLayoutEffect } from "react";

export function NotFound({ host: _host }: { host: string }): ReactElement {
  const { t } = useLocale();
  const pathname = usePathname() ?? "";
  const isBookingSuccessPage = pathname.startsWith("/booking");
  let heading = "This booking page is unavailable";
  if (isBookingSuccessPage) heading = "Booking not found";

  useLayoutEffect(() => {
    window.CalComPageStatus = "404";
  }, []);

  return (
    <div
      className="min-h-screen overflow-hidden bg-[#061126] px-4 py-8 text-white sm:px-8 sm:py-12"
      data-testid="404-page">
      <main className="relative mx-auto grid min-h-[min(680px,calc(100vh-4rem))] max-w-6xl overflow-hidden rounded-[2rem] border border-white/10 bg-[#0c1c42] shadow-[0_32px_120px_rgba(0,0,0,0.55)] lg:grid-cols-[0.92fr_1.08fr]">
        <section className="relative z-10 flex flex-col justify-center px-7 py-12 sm:px-12 lg:px-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(232,57,117,0.2),transparent_32%),radial-gradient(circle_at_60%_90%,rgba(35,91,220,0.28),transparent_45%)]" />
          <div className="relative max-w-md">
            <p className="font-semibold text-pink-100/85 text-xs uppercase tracking-[0.22em]">
              {t("error_404")}
            </p>
            <p className="mt-7 font-black text-7xl text-pink-300/90 leading-none tracking-[-0.08em]">404</p>
            <h1 className="mt-5 font-black text-4xl leading-[0.96] tracking-[-0.045em] sm:text-5xl">
              {heading}
            </h1>
            <p className="mt-5 max-w-sm text-base text-slate-200 leading-7">
              Sparkle checked every time slot. This one has wandered off, but you can return to WorkMate Cal.
            </p>
            <Link
              href="/"
              className="mt-9 inline-flex items-center rounded-full bg-[#e93d78] px-5 py-3 font-semibold text-white shadow-[0_12px_28px_rgba(233,61,120,0.32)] transition hover:bg-[#ff5890]">
              Go to WorkMate Cal <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>
        </section>
        <section className="relative min-h-[350px] overflow-hidden border-white/10 border-t lg:min-h-0 lg:border-t-0 lg:border-l">
          <video
            autoPlay
            className="absolute inset-0 h-full w-full object-cover"
            loop
            muted
            playsInline
            poster="/workmate-assets/404/laughingdog.jpg">
            <source src="/workmate-assets/404/laughingdog.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(12,28,66,0.36),transparent_36%),linear-gradient(0deg,rgba(3,8,21,0.52),transparent_40%)]" />
          <p className="absolute right-6 bottom-6 rounded-full border border-white/15 bg-[#07112a]/70 px-4 py-2 font-semibold text-xs text-white backdrop-blur">
            Sparkle says: try another route
          </p>
        </section>
      </main>
    </div>
  );
}
